"use client";

import { Card, CardContent } from "@/shadcn/ui/card";
import { ChevronRight, MapPin } from "lucide-react";

import { Comedian } from "@/data/comedians";
import ComedianAvatarWithFollowCircle from "@/components/signin/SuggestedComediansForm/ComedianAvatarWithFollowCircle";
import Image from "next/image";
import Link from "next/link";
import { RichTextContent } from "@punchup/venues/RichTextContent";
import { ShowWithComedian } from "@/data/shows";
import { cn } from "@/components/common/utils";
import { getCDNImageURL } from "@/data/images";
import { useState } from "react";

interface EventAboutSectionProps {
  show: ShowWithComedian;
  comedian: Comedian;
  posterImage?: string;
  tixologiData?: any;
  /** Server-sanitized description HTML (PUN-5353) — the sanitizer is server-only
   *  (cheerio), so it's computed on the server page and passed down. Empty →
   *  the description renders nothing. */
  sanitizedDescriptionHtml?: string;
  sanitizedTixologiDescriptionHtml?: string;
  allComedians: Comedian[];
  isMultiComic: boolean;
  isPunchupComedian: boolean;
  followedComedianIds: string[];
  isFollowing: boolean;
  onComediansDrawerOpen: () => void;
}

export default function EventAboutSection({
  show,
  comedian,
  posterImage,
  tixologiData,
  sanitizedDescriptionHtml,
  sanitizedTixologiDescriptionHtml,
  allComedians,
  isMultiComic,
  isPunchupComedian,
  followedComedianIds,
  isFollowing,
  onComediansDrawerOpen,
}: EventAboutSectionProps) {
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  return (
    <div className="lg:col-span-2 space-y-8 order-2 lg:order-1">
      {/* Event Poster (if available from Tixologi or show data) */}
      {posterImage && (
        <Card>
          <CardContent className="p-0">
            <div className="relative aspect-[3/4] md:aspect-[2/3]">
              <Image
                src={posterImage}
                alt={`${show.title || comedian.display_name} poster`}
                fill
                className="object-contain rounded-lg"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* About the Show */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold font-sans mb-4">
            About This Show
          </h2>
          {/* The server seeds + sanitizes metadata_text (a legacy plain-text row
              becomes <p>/<br>), so RichTextContent is the only render path. */}
          {sanitizedDescriptionHtml && (
            <div className="mb-4">
              {/* text-gray-700 keeps the pre-PUN-5353 show-description look. */}
              <RichTextContent
                html={sanitizedDescriptionHtml}
                className="text-gray-700"
              />
            </div>
          )}
          {sanitizedTixologiDescriptionHtml && (
            <div className="mb-4">
              <RichTextContent
                html={sanitizedTixologiDescriptionHtml}
                className="text-gray-700"
              />
            </div>
          )}

          {/* Venue Details */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold font-sans mb-3">
              Venue Details
            </h3>
            <div className="space-y-2 text-gray-700">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 text-gray-500" />
                <div>
                  <p className="font-medium">{show.venue}</p>
                  <p className="text-sm">{show.location}</p>
                  {tixologiData?.venue?.address && (
                    <p className="text-sm text-gray-600">
                      {tixologiData.venue.address}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Show Policies */}
          {tixologiData?.policies && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold font-sans mb-3">
                Event Policies
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                {tixologiData.policies.age_restriction && (
                  <p>
                    • Age Restriction: {tixologiData.policies.age_restriction}
                  </p>
                )}
                {tixologiData.policies.door_time && (
                  <p>• Doors Open: {tixologiData.policies.door_time}</p>
                )}
                {tixologiData.policies.show_length && (
                  <p>• Show Length: {tixologiData.policies.show_length}</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* About the Comedian */}
      <Card>
        <CardContent className="p-6">
          {isMultiComic ? (
            // Multi-comedian showcase layout
            <>
              <h2 className="text-2xl mb-6">
                <span className="font-normal">About</span>{" "}
                <span className="font-bold">the Comedians</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mb-6">
                {allComedians.slice(0, 6).map((comic) => (
                  <div key={comic.id} className="flex flex-col items-center">
                    <div className="relative mb-3">
                      <ComedianAvatarWithFollowCircle
                        comedianId={comic.id}
                        comedianImgUrl={
                          comic.about_img
                            ? comic.about_img.startsWith("http")
                              ? comic.about_img
                              : getCDNImageURL(comic.about_img)
                            : undefined
                        }
                        comedianName={comic.display_name || ""}
                        size="lg"
                        isFollowed={followedComedianIds.includes(comic.id)}
                        isNotPunchupComedian={!comic.is_punchup_verified}
                        hideComedianName={true}
                      />
                    </div>
                    <p className="text-sm font-medium text-center">
                      {comic.display_name}
                    </p>
                  </div>
                ))}
              </div>
              {allComedians.length > 6 && (
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onComediansDrawerOpen();
                  }}
                  className="text-orange-500 hover:text-orange-600 text-base font-medium"
                >
                  View all {allComedians.length} comedians →
                </Link>
              )}
            </>
          ) : (
            // Single comedian layout
            <>
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl">
                  <span className="font-normal">About</span>{" "}
                  <span className="font-bold">{comedian.display_name}</span>
                </h2>
                {isPunchupComedian && comedian.slug && (
                  <Link
                    href={`/${comedian.slug}`}
                    className="text-orange-500 hover:text-orange-600 text-base font-medium flex items-center gap-1"
                  >
                    View Profile
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <ComedianAvatarWithFollowCircle
                    comedianId={comedian.id}
                    comedianImgUrl={
                      comedian.about_img
                        ? comedian.about_img.startsWith("http")
                          ? comedian.about_img
                          : getCDNImageURL(comedian.about_img)
                        : undefined
                    }
                    comedianName={comedian.display_name || ""}
                    size="lg"
                    isFollowed={isFollowing}
                    isNotPunchupComedian={!isPunchupComedian}
                    hideComedianName={true}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-2xl mb-4">
                    {comedian.display_name}
                  </h3>
                  {comedian.about && (
                    <div>
                      <p
                        className={cn(
                          "text-gray-700 leading-relaxed",
                          !isBioExpanded &&
                            comedian.about.length > 300 &&
                            "line-clamp-3"
                        )}
                      >
                        {comedian.about}
                      </p>
                      {comedian.about.length > 300 && (
                        <button
                          onClick={() => setIsBioExpanded(!isBioExpanded)}
                          className="text-orange-500 hover:text-orange-600 text-sm font-medium mt-2"
                        >
                          {isBioExpanded ? "Read Less" : "Read More"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
